import { useEffect, useState } from 'react';
import {
  Routes,
  Route,
  Link,
  Navigate,
  matchPath,
  useLocation,
} from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useAppSelector } from './hooks/hooks';
import { Spinner, Navbar, Nav, Container, Offcanvas } from 'react-bootstrap';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import Register from './pages/Register';
import { useGetUserOrganisations } from './hooks/useGetUserOrganisations';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedOrganisation } from './features/organisation/organisationSlice';
import type { UserOrganisation } from './types/types';
import Suppliers from './pages/Suppliers';
import SupplierDetails from './pages/SupplierDetails';

import logo from '../src/assets/LogoNavbar.png';
import ImportBulkSuppliers from './pages/ImportBulkSuppliers';
import RiskAssessment from './pages/RiskAssessment';
import Questionnaires from './pages/Questionnaires';
import Questionnaire from './pages/Questionnaire';
import Assessment from './pages/Assessment';
import Invite from './pages/Invite';
import AuthCallback from './pages/AuthCallback';
import CheckSupplierOrganisation from './components/supplier/CheckSupplierOrganisation';
import SelfAssessment from './pages/SelfAssessment';
import QuestionnaireBuilder from './components/questionnaireBuilder/QuestionnaireBuilder';
import AssessmentResult from './pages/AssessmentResult';
import SupplierAssessments from './pages/SupplierAssessments';
import MyCompliance from './pages/MyCompliance';
import ActionPlan from './pages/ActionPlan';
import ViewPublishedReport from './pages/ViewPublishedReport';
import MySettings from './pages/MySettings';
import { tracking } from './util/tracking';
import { SubscriptionCallback } from './pages/SubscriptionCallback';
import SupplierProfile from './pages/SupplierProfile';
import MyProfile from './pages/MyProfile';
import SupplyChain from './pages/SupplyChain';
import SupplyChainDocumentUpload from './pages/SupplyChainDocumentUpload';
import SupplierProfilePreview from './pages/SupplierProfilePreview';
import SupplierProfilePublic from './pages/SupplierProfilePublic';
import SupplierProfileBuilder from './components/supplier/SupplierProfileBuilder_NEW';
import { hasNoProperties } from './util/util';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import NotFound from './pages/NotFound';
import DataProcessingAgreement from './pages/DataProcessingAgreement';

function App() {
  const { logout } = useAuth();

  const { isLoading, user } = useAppSelector((state) => state.auth);
  const {
    // organisations,
    // isLoading: isOrganisationsLoading,
    refetch: reloadOrganisations,
  } = useGetUserOrganisations(user?.id);
  const [showSidebar, setShowSidebar] = useState(false);

  const toggleSidebar = () => setShowSidebar(!showSidebar);
  const selectedOrganisation: UserOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  );
  const dispatch = useDispatch();
  const location = useLocation();

  const fullscreenRoutes = [
    '/supplier-profile/:profileId/preview',
    '/assessmentResult/:assessmentId',
    '/supplier/:profileId',
    '/supply-chain-document-upload/:accessToken',
  ];
  const isFullscreenRoute = fullscreenRoutes.some((path) =>
    matchPath({ path, end: true }, location.pathname),
  );
  const showAppShell = Boolean(user) && !isFullscreenRoute;

  useEffect(() => {
    if (isFullscreenRoute && showSidebar) {
      setShowSidebar(false);
    }
  }, [isFullscreenRoute, showSidebar]);

  // TODO: this needs to be built based on the account data
  const navItems = [
    { name: 'Dashboard', icon: 'bi-house', href: '/dashboard' },
    { name: 'Suppliers', icon: 'bi-people', href: '/suppliers' },
    {
      name: 'Risk Assessment',
      icon: 'bi-building-check',
      href: '/risk-assessment',
    },
    {
      name: 'Preventive Actions',
      icon: 'bi-clipboard-check',
      href: '/preventive-actions',
    },
    {
      name: 'Reports',
      icon: 'bi-bar-chart',
      href: '/reports',
    },
    {
      name: 'Questionnaires',
      icon: 'bi-file-text',
      href: '/questionnaires',
    },
  ];

  const navItemsSupplier = [
    { name: 'Dashboard', icon: 'bi-house', href: '/dashboard' },
    {
      name: 'Questionnaires',
      icon: 'bi-file-text',
      href: '/self-assessments',
    },
    {
      name: 'Assessments',
      icon: 'bi-bar-chart',
      href: '/assessments',
    },
    {
      name: 'Profile',
      icon: 'bi-person-badge',
      href: '/my-profile',
    },
    {
      name: 'My Compliance',
      icon: 'bi-clipboard-check',
      href: '/my-compliance',
    },
    { name: 'Supply Chain', icon: 'bi-diagram-3', href: '/supply-chain' },
  ];
  useEffect(() => {
    const initializeSession = async () => {
      // 1. Check if we have already logged a start for THIS specific tab session
      const hasStarted = sessionStorage.getItem(
        'supplypassport_session_logged',
      );

      if (!hasStarted) {
        await tracking.log('INTERNAL', 'session_start', {
          referrer: document.referrer,
          screen_resolution: `${window.screen.width}x${window.screen.height}`,
          user_agent: navigator.userAgent,
        });

        // 2. Set the flag so it doesn't fire again on re-renders or refreshes
        sessionStorage.setItem('supplypassport_session_logged', 'true');
      }
    };

    initializeSession();
  }, []);

  const menuItems =
    selectedOrganisation?.role === 'supplier' ? navItemsSupplier : navItems;

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  const showCheckSupplierOrganisation = Boolean(
    selectedOrganisation?.role === 'supplier' &&
    (selectedOrganisation?.organisation?.status === 'pending_confirmation' ||
      !selectedOrganisation?.organisation?.name ||
      hasNoProperties(selectedOrganisation?.organisation)),
  );

  // if (
  //   (!selectedOrganisation ||
  //     selectedOrganisation.organisation?.status !== 'pending_confirmation') &&
  //   selectedOrganisation?.organisation?.name !== null
  // ) {
  //   return null; // Don't show modal if no organisation or not pending
  // }

  return (
    <>
      {showAppShell && (
        <>
          {/* Top Navbar */}
          <Navbar bg="dark" variant="dark" expand="lg" fixed="top">
            <Container fluid>
              <Navbar.Brand as={Link} to="/dashboard">
                <img src={logo} alt="SupplyPassport Logo" height="30" />
              </Navbar.Brand>
              <Navbar.Toggle
                aria-controls="offcanvasSidebar"
                onClick={toggleSidebar}
              />
            </Container>
          </Navbar>

          {/* Offcanvas sidebar (mobile + tablet) */}
          <Offcanvas
            show={showSidebar}
            onHide={toggleSidebar}
            responsive="lg"
            id="offcanvasSidebar"
            className="bg-body-tertiary position-fixed d-lg-none"
          >
            <Offcanvas.Header closeButton className="border-bottom">
              <Offcanvas.Title>Menu</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <Nav className="flex-column">
                {menuItems.map((item) => (
                  <Nav.Link
                    key={item.name}
                    as={Link}
                    to={item.href}
                    className="text-dark"
                    onClick={() => setShowSidebar(false)}
                  >
                    <i className={`${item.icon} me-2`}></i>
                    {item.name}
                  </Nav.Link>
                ))}
                <hr />
                <Nav.Link className="text-dark mb-1">
                  <i className="bi-person-circle me-2"></i>
                  {`${user?.profile?.first_name} ${user?.profile?.last_name}`}
                </Nav.Link>
                <div className="text-dark mb-3 ms-3">
                  <i className="bi-building me-2"></i>
                  {selectedOrganisation?.organisation?.name}
                </div>
                {/* <p className="mb-0">
                  <select
                    className="form-select"
                    aria-label="organisation select"
                    disabled={isOrganisationsLoading}
                    value={selectedOrganisation?.organisation?.id ?? ''}
                    onChange={(e) => {
                      const org = organisations.find(
                        (o: UserOrganisation) =>
                          o.organisation?.id === e.target.value,
                      );
                      if (org) {
                        dispatch(setSelectedOrganisation(org));
                      }
                    }}
                  >
                    {organisations.map((o) => {
                      return (
                        <option
                          key={o.organisation?.id}
                          value={o.organisation?.id}
                        >
                          {o.organisation?.name}
                        </option>
                      );
                    })}
                  </select>
                </p> */}
                <Nav.Link
                  className="text-dark-emphasis mb-1 mt-2"
                  href="/settings"
                >
                  <i className="bi-gear me-2"></i>
                  Settings
                </Nav.Link>
                <Nav.Link
                  className="text-dark-emphasis mb-1"
                  onClick={() => {
                    logout();
                  }}
                >
                  <i className="bi-box-arrow-right me-2"></i>
                  Logout
                </Nav.Link>
              </Nav>
            </Offcanvas.Body>
          </Offcanvas>

          {/* Sidebar (desktop only, no duplicate header) */}
          <div
            className="d-none d-lg-flex flex-column vh-100 bg-body-tertiary position-fixed"
            style={{ width: '250px', top: '56px' }}
          >
            <Nav className="flex-column p-3" variant="pills">
              {menuItems.map((item) => (
                <Nav.Link
                  key={item.name}
                  as={Link}
                  to={item.href}
                  className="text-dark mb-1"
                >
                  <i className={`${item.icon} me-2`}></i>
                  {item.name}
                </Nav.Link>
              ))}
              <hr />
              <div className="text-dark mb-3 ms-3">
                <i className="bi-person-circle me-2"></i>
                {`${user?.profile?.first_name} ${user?.profile?.last_name}`}
              </div>
              <div className="text-dark mb-3 ms-3">
                <i className="bi-building me-2"></i>
                {selectedOrganisation?.organisation?.name}
              </div>
              {/* <p className="mb-0">
                <select
                  className="form-select"
                  aria-label="organisation select"
                  disabled={isOrganisationsLoading}
                  value={selectedOrganisation?.organisation?.id ?? ''}
                  onChange={(e) => {
                    const org = organisations.find(
                      (o: UserOrganisation) =>
                        o.organisation?.id === e.target.value
                    );
                    if (org) {
                      dispatch(setSelectedOrganisation(org));
                    }
                  }}
                >
                  {organisations.map((o) => {
                    return (
                      <option
                        key={o.organisation?.id}
                        value={o.organisation?.id}
                      >
                        {o.organisation?.name}
                      </option>
                    );
                  })}
                </select>
              </p> */}
              <Nav.Link
                className="text-dark-emphasis mb-1 mt-2"
                href="/settings"
              >
                <i className="bi-gear me-2"></i>
                Settings
              </Nav.Link>
              <Nav.Link
                className="text-dark-emphasis mb-1"
                onClick={() => {
                  logout();
                }}
              >
                <i className="bi-box-arrow-right me-2"></i>
                Logout
              </Nav.Link>
            </Nav>
          </div>
        </>
      )}

      {/* Main content */}
      <div
        className={`flex-grow-1 ${
          showAppShell ? 'mt-5 content-with-sidebar p-4' : ''
        }`}
      >
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suppliers"
            element={
              <ProtectedRoute>
                <Suppliers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suppliers/:supplierId"
            element={
              <ProtectedRoute>
                <SupplierDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suppliers/bulk-import-suppliers"
            element={
              <ProtectedRoute>
                <ImportBulkSuppliers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/supplier-profile/:profileId/preview"
            element={
              <ProtectedRoute>
                <SupplierProfilePreview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/supplier/:profileId"
            element={<SupplierProfilePublic />}
          />
          <Route
            path="/supply-chain-document-upload/:accessToken"
            element={<SupplyChainDocumentUpload />}
          />
          <Route
            path="/risk-assessment"
            element={
              <ProtectedRoute>
                <RiskAssessment />
              </ProtectedRoute>
            }
          />
          <Route
            path="/questionnaires"
            element={
              <ProtectedRoute>
                <Questionnaires />
              </ProtectedRoute>
            }
          />
          <Route
            path="/questionnaire/:templateId"
            element={
              <ProtectedRoute>
                <Questionnaire />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-supplier-profile/:slug"
            element={
              <ProtectedRoute>
                <SupplierProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-profile"
            element={
              <ProtectedRoute>
                <MyProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/supply-chain"
            element={
              <ProtectedRoute>
                <SupplyChain />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-profile/builder"
            element={
              <ProtectedRoute>
                <SupplierProfileBuilder />
              </ProtectedRoute>
            }
          />
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path="/register/free"
            element={<Navigate to="/register?selectedPlan=free" replace />}
          />
          <Route
            path="/terms-or-service"
            element={
              <PublicRoute>
                <TermsOfService />
              </PublicRoute>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <PublicRoute>
                <PrivacyPolicy />
              </PublicRoute>
            }
          />
          <Route
            path="/data-processing-agreement"
            element={
              <PublicRoute>
                <DataProcessingAgreement />
              </PublicRoute>
            }
          />
          <Route
            path="/self-assessments"
            element={
              <ProtectedRoute>
                <SelfAssessment />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessments"
            element={
              <ProtectedRoute>
                <SupplierAssessments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/questionnaires/:templateId/edit"
            element={
              <ProtectedRoute>
                <QuestionnaireBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-compliance"
            element={
              <ProtectedRoute>
                <MyCompliance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/action-plan/:planId"
            element={
              <ProtectedRoute>
                <ActionPlan />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <MySettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessmentResult/:assessmentId"
            element={<AssessmentResult />}
          />
          <Route path="/assessment/:assessmentId" element={<Assessment />} />
          <Route path="/invite/:inviteId" element={<Invite />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/subscription-callback"
            element={<SubscriptionCallback />}
          />
          <Route
            path="/public/report/:reportId"
            element={<ViewPublishedReport />}
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      <CheckSupplierOrganisation
        show={showCheckSupplierOrganisation}
        selectedOrganisation={selectedOrganisation}
        onHide={() => {}}
        onUpdate={(updatedOrg) => {
          tracking.log('INTERNAL', 'organisation_updated', {
            org: JSON.stringify(updatedOrg.organisation),
          });
          dispatch(setSelectedOrganisation(updatedOrg));
          reloadOrganisations();
        }}
      />
    </>
  );
}

export default App;
